import Fetcher from "../Fetcher.ts";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { MailboxTabular, Tabular, VendorRelationship } from "./RFQ";
import type { RFQMailbox } from "./RFQMailbox";

export type AwardExportFormat = "csv" | "xlsx" | "pdf";

export type RFQAwardItem = {
    id: number;
    rfqLineItemRowId?: number | null;
    quoteLineItemRowId: number;
    rfqLineItemRow?: Tabular["rows"][number] | null;
    quoteLineItemRow: Tabular["rows"][number];
};

export type RFQAward = {
    id: number;
    rationale?: string;
    createdAt: string;
    awardedItems: RFQAwardItem[];
    mailboxTable: MailboxTabular & {
        tabularDataStore: Tabular;
        mailbox: RFQMailbox & {
            vendorRelationship: VendorRelationship;
        };
    };
};

export type CreateAwardParams = {
    mailboxId: number;
    mailboxTableId: number;
    rationale?: string;
    awardItems?: { rfqLineItemRowId?: number | null; quoteLineItemRowId: number }[];
    awardAll?: boolean;
};

export type UpdateAwardParams = {
    awardId: number;
    rationale?: string;
    awardItems?: { rfqLineItemRowId?: number | null; quoteLineItemRowId: number }[];
};

const f = Fetcher.default();

export default {
    getRFQAwards: (rfqPublicId: string) => {
        return f.get<RFQAward[]>(`/v2/rfqs/${rfqPublicId}/awards`);
    },

    createAward: (params: CreateAwardParams) => {
        const { mailboxId, mailboxTableId, ...body } = params;
        return f.post<RFQAward>(`/mailboxes/${mailboxId}/tables/${mailboxTableId}/awards`, { body });
    },

    updateAward: (params: UpdateAwardParams) => {
        const { awardId, ...body } = params;
        return f.patch<RFQAward>(`/awards/${awardId}`, { body });
    },

    deleteAward: (awardId: number) => {
        return f.delete(`/awards/${awardId}`, { body: {} });
    },

    updateAwardRationale: (awardId: number, rationale: string): Promise<void> => {
        return f.patch(`/awards/${awardId}`, { body: { rationale } });
    },

    /**
     * Export awarded line items as CSV, XLSX, or PDF.
     * Returns a Blob that can be used to trigger a file download.
     */
    exportAwardItems: async (awardId: number, format: AwardExportFormat): Promise<{ blob: Blob; filename: string }> => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/awards/${awardId}/export?format=${format}`, {
            method: "GET",
            headers: getHeaders(),
        });

        if (!response.ok) {
            throw new Error("Failed to export award items");
        }

        // Get filename from Content-Disposition header if available
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `award-${awardId}-line-items.${format}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (filenameMatch?.[1]) {
                filename = filenameMatch[1];
            }
        }

        const blob = await response.blob();
        return { blob, filename };
    },
};
